/* qp-home.js — landing page behavior (Wave 3 / signed-proof build).
   Three small custom elements. No library.

   <qp-home-hero>       slideshow: numeral indicators, 6s auto-advance,
                        paused on hover/focus. Under prefers-reduced-motion
                        the element flips data-autoplay to "false" and never
                        starts the timer — manual (indicator click) only.

   <qp-home-rail>       ride-now rail: ink prev/next arrows scroll the
                        native scroll-snap track by one card width.
                        NO autoplay.

   <qp-print-deck>      custom-band specimen-print polaroid deck (R7):
                        swipe/tap on the stage or the next button sends
                        the top card to the back of the stack (loops).
                        Depth is the data-qp-deck-depth attribute (CSS
                        styles it); a mono "N / M" counter + aria-live
                        caption announce track the top card. NO autoplay
                        — advance is user-driven only (the single
                        setTimeout is the 240ms leave-animation hand-off,
                        skipped entirely under prefers-reduced-motion,
                        which flips data-motion to "false" for an
                        instant swap). <2 cards: no wiring at all.

   <qp-owners-rotator>  curated quote rotator (owners wall): AUTO-ROTATES
                        the visible quote via the hidden attribute (R6:
                        the ← → arrow buttons are retired) — mirrors
                        <qp-home-hero>'s pattern exactly: ~6s auto-
                        advance, paused on hover/focus, wraps around.
                        Under prefers-reduced-motion the element flips
                        data-autoplay to "false" and never starts the
                        timer (manual-free — first quote just stays).
                        data-motion separately gates the CSS cross-fade
                        (flips to "false" under reduced motion too, so
                        the swap is instant when a timer does run).
*/
(function () {
  'use strict';

  function prefersReducedMotion() {
    try {
      return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    } catch (e) {
      return false;
    }
  }

  if (!customElements.get('qp-home-hero')) {
    customElements.define(
      'qp-home-hero',
      class QpHomeHero extends HTMLElement {
        connectedCallback() {
          this._slides = Array.prototype.slice.call(this.querySelectorAll('[data-qp-hero-slide]'));
          this._dots = Array.prototype.slice.call(this.querySelectorAll('[data-qp-hero-dot]'));
          this._index = 0;
          this._timer = null;

          if (prefersReducedMotion()) {
            this.dataset.autoplay = 'false';
          }

          this._dots.forEach(
            function (dot, i) {
              dot.addEventListener(
                'click',
                function () {
                  this._goTo(i);
                  this._restart();
                }.bind(this)
              );
            }.bind(this)
          );

          this._onStop = this._stop.bind(this);
          this._onRestart = this._restart.bind(this);
          this.addEventListener('mouseenter', this._onStop);
          this.addEventListener('mouseleave', this._onRestart);
          this.addEventListener('focusin', this._onStop);
          this.addEventListener('focusout', this._onRestart);

          this._goTo(0);
          this._restart();
        }

        disconnectedCallback() {
          this._stop();
          this.removeEventListener('mouseenter', this._onStop);
          this.removeEventListener('mouseleave', this._onRestart);
          this.removeEventListener('focusin', this._onStop);
          this.removeEventListener('focusout', this._onRestart);
        }

        _goTo(i) {
          if (!this._slides.length) return;
          this._index = (i + this._slides.length) % this._slides.length;

          this._slides.forEach(
            function (slide, idx) {
              var active = idx === this._index;
              slide.classList.toggle('is-active', active);
              if (active) {
                slide.removeAttribute('aria-hidden');
              } else {
                slide.setAttribute('aria-hidden', 'true');
              }
              /* background video: only the active slide plays; nothing plays
                 under reduced motion (poster stays — the first slide's
                 autoplay attribute is reconciled here too) */
              var video = slide.querySelector('video.qp-home-hero__video');
              if (video) {
                if (active && !prefersReducedMotion()) {
                  var played = video.play();
                  if (played && played.catch) played.catch(function () {});
                } else {
                  video.pause();
                }
              }
            }.bind(this)
          );

          this._dots.forEach(
            function (dot, idx) {
              var active = idx === this._index;
              dot.classList.toggle('is-active', active);
              dot.setAttribute('aria-selected', active ? 'true' : 'false');
            }.bind(this)
          );
        }

        _stop() {
          if (this._timer) {
            clearInterval(this._timer);
            this._timer = null;
          }
        }

        _restart() {
          this._stop();
          if (this.dataset.autoplay === 'false') return;
          if (this._slides.length < 2) return;
          var interval = parseInt(this.dataset.interval, 10) || 6000;
          var self = this;
          this._timer = setInterval(function () {
            self._goTo(self._index + 1);
          }, interval);
        }
      }
    );
  }

  if (!customElements.get('qp-home-rail')) {
    customElements.define(
      'qp-home-rail',
      class QpHomeRail extends HTMLElement {
        connectedCallback() {
          this._track = this.querySelector('[data-qp-rail-track]');
          this._prev = this.querySelector('[data-qp-rail-prev]');
          this._next = this.querySelector('[data-qp-rail-next]');
          if (!this._track) return;

          this._onPrev = this._scroll.bind(this, -1);
          this._onNext = this._scroll.bind(this, 1);
          if (this._prev) this._prev.addEventListener('click', this._onPrev);
          if (this._next) this._next.addEventListener('click', this._onNext);

          this._onScroll = this._updateArrows.bind(this);
          this._track.addEventListener('scroll', this._onScroll, { passive: true });
          this._updateArrows();
        }

        disconnectedCallback() {
          if (this._track) this._track.removeEventListener('scroll', this._onScroll);
          if (this._prev) this._prev.removeEventListener('click', this._onPrev);
          if (this._next) this._next.removeEventListener('click', this._onNext);
        }

        _scroll(dir) {
          if (!this._track) return;
          var item = this._track.querySelector('[data-qp-rail-item]');
          var amount = item ? item.getBoundingClientRect().width + 16 : this._track.clientWidth * 0.8;
          this._track.scrollBy({ left: dir * amount, behavior: 'smooth' });
        }

        _updateArrows() {
          if (!this._track) return;
          var max = this._track.scrollWidth - this._track.clientWidth - 1;
          if (this._prev) this._prev.disabled = this._track.scrollLeft <= 0;
          if (this._next) this._next.disabled = this._track.scrollLeft >= max;
        }
      }
    );
  }

  if (!customElements.get('qp-print-deck')) {
    customElements.define(
      'qp-print-deck',
      class QpPrintDeck extends HTMLElement {
        connectedCallback() {
          this._cards = Array.prototype.slice.call(this.querySelectorAll('[data-qp-deck-card]'));
          this._stage = this.querySelector('[data-qp-deck-stage]');
          this._next = this.querySelector('[data-qp-deck-next]');
          this._counter = this.querySelector('[data-qp-deck-counter]');
          this._announce = this.querySelector('[data-qp-deck-announce]');
          this._index = 0; /* which card is on top */
          this._pending = null; /* leave-animation hand-off timeout */
          this._pointerStart = null;

          if (prefersReducedMotion()) {
            this.dataset.motion = 'false';
          }

          /* blank-safe: a single card is a static specimen — no wiring */
          if (this._cards.length < 2) return;

          this._onNext = this._onNextClick.bind(this);
          if (this._next) this._next.addEventListener('click', this._onNext);

          this._onPointerDown = this._pointerDown.bind(this);
          this._onPointerUp = this._pointerUp.bind(this);
          if (this._stage) {
            this._stage.addEventListener('pointerdown', this._onPointerDown);
            this._stage.addEventListener('pointerup', this._onPointerUp);
          }

          this._apply();
        }

        disconnectedCallback() {
          if (this._pending) {
            clearTimeout(this._pending);
            this._pending = null;
          }
          if (this._next) this._next.removeEventListener('click', this._onNext);
          if (this._stage) {
            this._stage.removeEventListener('pointerdown', this._onPointerDown);
            this._stage.removeEventListener('pointerup', this._onPointerUp);
          }
        }

        _onNextClick() {
          this._advance();
        }

        _pointerDown(e) {
          this._pointerStart = { x: e.clientX, y: e.clientY };
        }

        _pointerUp(e) {
          if (!this._pointerStart) return;
          var dx = e.clientX - this._pointerStart.x;
          var dy = e.clientY - this._pointerStart.y;
          this._pointerStart = null;
          if (Math.abs(dx) > 30 && Math.abs(dx) > Math.abs(dy)) {
            /* horizontal swipe */
            this._advance();
          } else if (Math.abs(dx) < 8 && Math.abs(dy) < 8) {
            /* tap/click on the stage */
            this._advance();
          }
          /* anything in between (e.g. a vertical scroll drag) is ignored */
        }

        _advance() {
          if (this._cards.length < 2 || this._pending) return;
          var top = this._cards[this._index];
          var self = this;

          if (this.dataset.motion === 'false') {
            /* reduced motion: instant swap, zero fly animation */
            this._index = (this._index + 1) % this._cards.length;
            this._apply();
            return;
          }

          /* ~240ms transform-only leave, then tuck behind (CSS is-leaving) */
          top.classList.add('is-leaving');
          this._pending = setTimeout(function () {
            top.classList.remove('is-leaving');
            self._pending = null;
            self._index = (self._index + 1) % self._cards.length;
            self._apply();
          }, 240);
        }

        _apply() {
          var n = this._cards.length;
          this._cards.forEach(
            function (card, idx) {
              var depth = (idx - this._index + n) % n;
              card.setAttribute('data-qp-deck-depth', String(depth));
            }.bind(this)
          );
          if (this._counter) {
            this._counter.textContent = this._index + 1 + ' / ' + n;
          }
          if (this._announce) {
            var plate = this._cards[this._index].querySelector('figcaption');
            this._announce.textContent = plate ? plate.textContent : '';
          }
        }
      }
    );
  }

  if (!customElements.get('qp-owners-rotator')) {
    customElements.define(
      'qp-owners-rotator',
      class QpOwnersRotator extends HTMLElement {
        connectedCallback() {
          this._quotes = Array.prototype.slice.call(this.querySelectorAll('[data-qp-owners-quote]'));
          this._index = 0;
          this._timer = null;
          for (var i = 0; i < this._quotes.length; i++) {
            if (!this._quotes[i].hidden) {
              this._index = i;
              break;
            }
          }

          if (prefersReducedMotion()) {
            this.dataset.motion = 'false';
            this.dataset.autoplay = 'false';
          }

          this._onStop = this._stop.bind(this);
          this._onRestart = this._restart.bind(this);
          this.addEventListener('mouseenter', this._onStop);
          this.addEventListener('mouseleave', this._onRestart);
          this.addEventListener('focusin', this._onStop);
          this.addEventListener('focusout', this._onRestart);

          this._restart();
        }

        disconnectedCallback() {
          this._stop();
          this.removeEventListener('mouseenter', this._onStop);
          this.removeEventListener('mouseleave', this._onRestart);
          this.removeEventListener('focusin', this._onStop);
          this.removeEventListener('focusout', this._onRestart);
        }

        _goTo(i) {
          if (!this._quotes.length) return;
          this._index = (i + this._quotes.length) % this._quotes.length; /* wraps around */
          this._quotes.forEach(
            function (quote, idx) {
              quote.hidden = idx !== this._index;
            }.bind(this)
          );
        }

        _stop() {
          if (this._timer) {
            clearInterval(this._timer);
            this._timer = null;
          }
        }

        _restart() {
          this._stop();
          if (this.dataset.autoplay === 'false') return;
          if (this._quotes.length < 2) return;
          var interval = parseInt(this.dataset.interval, 10) || 6000;
          var self = this;
          this._timer = setInterval(function () {
            self._goTo(self._index + 1);
          }, interval);
        }
      }
    );
  }
})();
